import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/PhpAuthContext';
import { useData } from '../../contexts/DataContext';
import { Button } from '../../components/ui/button';
import { toast } from '../../components/ui/use-toast';
import { 
  ArrowLeft, 
  Stethoscope
} from 'lucide-react';

import PersonalInformationSection from '../../components/patient/PersonalInformationSection';
import MedicalHistorySection from '../../components/patient/MedicalHistorySection';
import DentalHistorySection from '../../components/patient/DentalHistorySection';
import DoctorSelectionSection from '../../components/patient/DoctorSelectionSection';
import FileUploadSection from '../../components/patient/FileUploadSection';
import AdditionalNotesSection from '../../components/patient/AdditionalNotesSection';

const PatientForm = () => {
  const { user } = useAuth();
  const { addSubmission, doctors } = useData();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    emergencyContact: '',
    emergencyPhone: '',
    // Medical History fields (matching doctor dashboard)
    surgery_hospitalized: 'No',
    surgery_details: '',
    fever_cold_cough: 'No',
    symptoms_details: '',
    medical_problems: [],
    abnormal_bleeding: 'No',
    abnormal_bleeding_details: '',
    taking_medicine: 'No',
    medicine_details: '',
    allergic_medication: 'No',
    allergy_details: '',
    past_dental_history: '',
    // Legacy fields (kept for backward compatibility)
    allergies: '',
    medications: '',
    medicalConditions: '',
    previousSurgeries: '',
    lastDentalVisit: '',
    chief_complaint: '',
    pain_level: '',
    painDuration: '',
    previousDentalWork: '',
    symptoms: [],
    painLocation: '',
    painType: '',
    smokingStatus: '',
    alcoholConsumption: '',
    oralHygiene: '',
    doctor_id: '',
    additionalNotes: ''
  });

  const [files, setFiles] = useState([]);

  const handleSymptomChange = (symptom) => {
    setFormData(prev => ({
      ...prev,
      symptoms: prev.symptoms.includes(symptom)
        ? prev.symptoms.filter(s => s !== symptom)
        : [...prev.symptoms, symptom]
    }));
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...selectedFiles]);
    toast({ title: '🚧 File uploads are not implemented yet. This is a UI demonstration only. 🚀' });
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const painLevelInt = formData.pain_level ? parseInt(formData.pain_level, 10) : null;

      if (formData.pain_level && isNaN(painLevelInt)) {
        toast({
          title: 'Invalid Input',
          description: 'Pain level must be a number.',
          variant: 'destructive'
        });
        setLoading(false);
        return;
      }

      const submissionData = {
        patient_id: user.id,
        doctor_id: formData.doctor_id || null,
        chief_complaint: formData.chief_complaint,
        pain_level: painLevelInt,
        symptoms: formData.symptoms,
        form_data: formData,
      };

      const { error } = await addSubmission(submissionData);
      
      if (error) throw error;

      toast({
        title: 'Form submitted successfully!',
        description: 'Your dental health form has been sent to the doctor for review.',
      });

      navigate('/patient/dashboard');
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to submit form. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Dental Health Form - Dental Hub</title>
        <meta name="description" content="Complete your comprehensive dental health questionnaire to help your dentist provide the best care." />
      </Helmet>
      
      <div className="min-h-screen">
        {/* Header */}
        <header className="glass-effect border-b border-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center space-x-4">
                <Button
                  variant="ghost"
                  onClick={() => navigate('/patient/dashboard')}
                  className="text-white hover:text-blue-400"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Dashboard
                </Button>
              </div>
              
              <div className="flex items-center space-x-2">
                <img 
                  src="/logo-print.png" 
                  alt="Dental Hub Logo" 
                  className="h-10 md:h-12 w-auto object-contain"
                  onError={(e) => {
                    const fallback = e.target.nextElementSibling;
                    if (fallback) {
                      e.target.style.display = 'none';
                      fallback.style.display = 'block';
                    }
                  }}
                />
                <Stethoscope className="h-8 w-8 text-blue-400 hidden" />
                <span className="text-xl font-bold gradient-text">Dental Hub</span>
              </div>
            </div>
          </div>
        </header>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-8">
              <h1 className="text-3xl md:text-4xl font-bold gradient-text mb-2">
                Dental Health Questionnaire
              </h1>
              <p className="text-gray-300 text-lg">
                Please provide detailed information to help us serve you better
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              <PersonalInformationSection formData={formData} setFormData={setFormData} />
              <MedicalHistorySection formData={formData} setFormData={setFormData} />
              <DentalHistorySection formData={formData} setFormData={setFormData} handleSymptomChange={handleSymptomChange} />
              <DoctorSelectionSection formData={formData} setFormData={setFormData} doctors={doctors} />
              <FileUploadSection files={files} handleFileChange={handleFileChange} removeFile={removeFile} />
              <AdditionalNotesSection formData={formData} setFormData={setFormData} />

              {/* Submit Button */}
              <div className="flex justify-end space-x-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/patient/dashboard')}
                  className="border-gray-600 text-gray-300 hover:bg-gray-700"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="medical-gradient text-white hover:opacity-90 px-8"
                  disabled={loading}
                >
                  {loading ? 'Submitting...' : 'Submit Form'}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>

        {/* Footer */}
        <footer className="border-t border-gray-700 py-8 px-4 sm:px-6 lg:px-8 mt-8">
          <div className="max-w-7xl mx-auto text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <img 
                src="/logo.png" 
                alt="Dental Hub Logo" 
                className="h-8 w-auto object-contain"
                onError={(e) => {
                  const fallback = e.target.nextElementSibling;
                  if (fallback) {
                    e.target.style.display = 'none';
                    fallback.style.display = 'block';
                  }
                }}
              />
              <Stethoscope className="h-6 w-6 text-blue-400 hidden" />
              <span className="text-lg font-semibold gradient-text">Dental Hub</span>
            </div>
            <p className="text-gray-400 text-sm">
              © 2025 Dental Hub. All rights reserved. Developed and maintained by{' '}
              <a 
                href="https://www.neinus.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 underline transition-colors"
              >
                Neinus Technologies
              </a>
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default PatientForm;